import React from 'react';
import { cn } from '../lib/utils';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'pulse' | 'orbit' | 'quantum' | 'hyperdrive';
  color?: 'white' | 'purple' | 'blue' | 'pink' | 'yellow';
  className?: string;
  message?: string;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  variant = 'default',
  color = 'purple',
  className,
  message
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12',
    xl: 'w-16 h-16'
  };

  const colorClasses = {
    white: 'border-white/30 border-t-white',
    purple: 'border-purple-500/30 border-t-purple-400',
    blue: 'border-blue-500/30 border-t-blue-400',
    pink: 'border-pink-500/30 border-t-pink-400',
    yellow: 'border-yellow-500/30 border-t-yellow-400'
  };

  const renderSpinner = () => {
    switch (variant) {
      case 'pulse':
        return (
          <div className={cn(sizeClasses[size], 'relative')}>
            <div className={cn(
              'absolute inset-0 rounded-full animate-ping',
              color === 'white' && 'bg-white/20',
              color === 'purple' && 'bg-purple-500/20',
              color === 'blue' && 'bg-blue-500/20',
              color === 'pink' && 'bg-pink-500/20',
              color === 'yellow' && 'bg-yellow-500/20'
            )} />
            <div className={cn(
              'relative inset-0 rounded-full',
              color === 'white' && 'bg-white/40',
              color === 'purple' && 'bg-purple-500/40',
              color === 'blue' && 'bg-blue-500/40',
              color === 'pink' && 'bg-pink-500/40',
              color === 'yellow' && 'bg-yellow-500/40'
            )} />
          </div>
        );

      case 'orbit':
        return (
          <div className={cn(sizeClasses[size], 'relative')}>
            <div className="absolute inset-0 rounded-full border-2 border-transparent animate-spin"
                 style={{
                   borderTopColor: color === 'white' ? 'rgba(255,255,255,0.8)' :
                                  color === 'purple' ? 'rgba(168,85,247,0.8)' :
                                  color === 'blue' ? 'rgba(59,130,246,0.8)' :
                                  color === 'pink' ? 'rgba(236,72,153,0.8)' :
                                  'rgba(245,158,11,0.8)'
                 }} />
            <div className={cn(
              'absolute top-0 left-1/2 transform -translate-x-1/2 w-1 h-1 rounded-full',
              color === 'white' && 'bg-white',
              color === 'purple' && 'bg-purple-400',
              color === 'blue' && 'bg-blue-400',
              color === 'pink' && 'bg-pink-400',
              color === 'yellow' && 'bg-yellow-400'
            )} />
          </div>
        );

      case 'quantum':
        return (
          <div className={cn(sizeClasses[size], 'relative')}>
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className={cn(
                  'absolute inset-0 rounded-full border-2 animate-spin',
                  colorClasses[color]
                )}
                style={{
                  animationDelay: `${i * 0.15}s`,
                  animationDuration: '1.5s',
                  transform: `scale(${1 - i * 0.2})`,
                  opacity: 1 - i * 0.3
                }}
              />
            ))}
          </div>
        );

      case 'hyperdrive':
        return (
          <div className={cn(sizeClasses[size], 'relative')}>
            <div className="absolute inset-0 rounded-full">
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    'absolute w-full h-full rounded-full border-t-2',
                    colorClasses[color]
                  )}
                  style={{
                    transform: `rotate(${i * 45}deg)`,
                    animation: `spin 1s linear infinite`,
                    animationDelay: `${i * 0.125}s`,
                    opacity: 0.3 + (i * 0.1)
                  }}
                />
              ))}
            </div>
            <div className={cn(
              'absolute inset-2 rounded-full',
              color === 'white' && 'bg-white/10',
              color === 'purple' && 'bg-purple-500/10',
              color === 'blue' && 'bg-blue-500/10',
              color === 'pink' && 'bg-pink-500/10',
              color === 'yellow' && 'bg-yellow-500/10'
            )} />
          </div>
        );

      default:
        return (
          <div className={cn(
            sizeClasses[size],
            'border-2 rounded-full animate-spin',
            colorClasses[color]
          )} />
        );
    }
  };

  return (
    <div className={cn('flex flex-col items-center justify-center space-y-3', className)}>
      {renderSpinner()}
      {message && (
        <div className={cn(
          'text-sm font-medium animate-pulse',
          color === 'white' && 'text-white/80',
          color === 'purple' && 'text-purple-400',
          color === 'blue' && 'text-blue-400',
          color === 'pink' && 'text-pink-400',
          color === 'yellow' && 'text-yellow-400'
        )}>
          {message}
        </div>
      )}
    </div>
  );
};

export default LoadingSpinner;